import { Building2, Check, LoaderCircle, Search, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuthorityCustomerQuery, useCustomerDirectoryQuery, useTenantSessionQuery } from "@/api/query-hooks";
import { env } from "@/app/env";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { positiveIntegerSearch } from "@/lib/search";

export function useTenantClientScope(value: number | undefined) {
  const tenant = env.uiMode === "tenant";
  const session = useTenantSessionQuery(tenant);
  const clientId = positiveIntegerSearch(value);
  const ready = !tenant || session.isSuccess;

  return {
    tenant,
    clientId,
    required: false,
    ready,
    needsSelection: false,
    isLoading: tenant && session.isLoading,
    isError: tenant && session.isError,
    error: session.error,
    refetch: session.refetch
  };
}

export function ClientScopePicker({
  value,
  required,
  onChange
}: {
  value?: number;
  required: boolean;
  onChange: (value: number | undefined) => void;
}) {
  const { t } = useTranslation(["common", "registrationRequests"]);
  const inputId = useId();
  const listId = useId();
  const selectedId = positiveIntegerSearch(value) ?? 0;
  const selected = useAuthorityCustomerQuery(selectedId);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [queryText, setQueryText] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const directory = useCustomerDirectoryQuery({ q: queryText, mode: "all", limit: 24 }, open);
  const customers = useMemo(() => directory.data?.pages.flatMap((page) => page.items) ?? [], [directory.data]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setQueryText(draft.trim()), 250);
    return () => window.clearTimeout(timeout);
  }, [draft]);

  useEffect(() => {
    const node = loadMoreRef.current;
    const root = listRef.current;
    if (!open || !node || !root || !directory.hasNextPage || directory.isFetchingNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void directory.fetchNextPage();
    }, { root, rootMargin: "160px 0px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [directory.fetchNextPage, directory.hasNextPage, directory.isFetchingNextPage, open]);

  const selectedLabel = selected.data
    ? `${selected.data.name} · ${t("registrationRequests:clientNumber", { number: selected.data.number })}`
    : value
      ? t("clientScope.clientIdFallback", { id: value })
      : "";

  return (
    <div className="grid gap-1.5" data-testid="client-scope-picker">
      <Label htmlFor={inputId} className="text-xs text-muted-foreground">{t("clientScope.label")}</Label>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id={inputId}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          className="h-10 pl-9 pr-10"
          value={open ? draft : selectedLabel}
          placeholder={required ? t("registrationRequests:clientPlaceholder") : t("clientScope.allCustomers")}
          autoComplete="off"
          onFocus={() => {
            setDraft("");
            setQueryText("");
            setOpen(true);
          }}
          onChange={(event) => {
            setDraft(event.target.value);
            setOpen(true);
          }}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        />
        {(value || draft) ? (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2"
            aria-label={t("clientScope.clear")}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setDraft("");
              setQueryText("");
              onChange(undefined);
              setOpen(false);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        ) : null}
      </div>

      {open ? (
        <div
          id={listId}
          ref={listRef}
          role="listbox"
          aria-label={t("registrationRequests:clientResults")}
          className="mt-1 max-h-72 overflow-y-auto rounded-md border bg-panel text-foreground shadow-xl ring-1 ring-border"
          onMouseDown={(event) => event.preventDefault()}
        >
          {!required ? (
            <button
              type="button"
              role="option"
              aria-selected={!value}
              className="flex w-full items-center gap-3 border-b px-3 py-3 text-left hover:bg-accent"
              onClick={() => {
                onChange(undefined);
                setOpen(false);
              }}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">{!value ? <Check className="h-4 w-4 text-primary" /> : <Building2 className="h-4 w-4" />}</span>
              <span className="font-medium">{t("clientScope.allCustomers")}</span>
            </button>
          ) : null}
          {customers.map((customer) => {
            const chosen = selectedId === customer.id;
            return (
              <button
                key={customer.id}
                type="button"
                role="option"
                aria-selected={chosen}
                className="flex w-full min-w-0 items-center gap-3 border-b px-3 py-3 text-left last:border-b-0 hover:bg-accent"
                onClick={() => {
                  onChange(customer.id);
                  setDraft("");
                  setQueryText("");
                  setOpen(false);
                }}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">{chosen ? <Check className="h-4 w-4 text-primary" /> : <Building2 className="h-4 w-4" />}</span>
                <span className="min-w-0 flex-1">
                  <span className="block break-words font-medium">{customer.name}</span>
                  <span className="text-xs text-muted-foreground">{t("registrationRequests:clientNumber", { number: customer.number })}</span>
                </span>
                <Badge variant={customer.status === "ACTIVE" ? "success" : "warning"}>{customer.status}</Badge>
              </button>
            );
          })}
          {!directory.isLoading && !directory.isError && customers.length === 0 ? <p className="px-3 py-6 text-center text-sm text-muted-foreground">{t("registrationRequests:clientEmpty")}</p> : null}
          {directory.isError ? (
            <div className="grid gap-2 px-3 py-4 text-sm text-destructive">
              <span>{t("registrationRequests:clientLoadFailed")}</span>
              <Button type="button" size="sm" variant="outline" onClick={() => void directory.refetch()}>{t("actions.retry")}</Button>
            </div>
          ) : null}
          <div ref={loadMoreRef} className="flex min-h-10 items-center justify-center text-xs text-muted-foreground">
            {directory.isLoading || directory.isFetchingNextPage ? <><LoaderCircle className="mr-2 h-4 w-4 animate-spin" />{t("registrationRequests:clientLoading")}</> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
